import React from 'react';

/**
 * Collects the `name` of every control declared inside a tab.
 *
 * Reads the element tree rather than the DOM: a tab strip renders only the tab in front of the
 * user, so the fields of every other tab exist nowhere to be found. The elements do exist -- the
 * form builds them all when it renders, and the strip merely picks one set to mount -- which is
 * what makes a tab nobody has opened answerable for what it holds.
 *
 * What it cannot see is a field a child component renders for itself, since that element is not
 * built until the component is mounted. Such a component names the part of the payload it owns with
 * a `name` of its own, which is what the match below falls back to.
 *
 * @param {React.ReactNode} node - Anything that can be a child: an element, an array, a string.
 * @param {Set<string>} names - Collected into, in place.
 */
const _collectFieldNames = (node, names) => {
  React.Children.forEach(node, child => {
    if (!React.isValidElement(child)) return;

    if (typeof child.props.name === 'string') names.add(child.props.name);

    _collectFieldNames(child.props.children, names);
  });
};

/**
 * Whether any error the server reported belongs to a field on this tab.
 *
 * Nothing is walked until there is an error to place, so the common case -- a form nobody has
 * submitted yet, or one that saved cleanly -- costs a single check of an empty object.
 *
 * @param {React.ReactNode} children - The tab's content, as written in the form.
 * @param {Record<string, string>} [formErrors] - Field-keyed errors from a 400 response.
 * @returns {boolean}
 */
export const hasFieldError = (children, formErrors) => {
  const keys = Object.keys(formErrors || {});
  if (keys.length === 0) return false;

  const names = new Set();
  _collectFieldNames(children, names);

  // A key naming a row of a nested list -- `attribute_values.0.attribute_value_name` -- belongs to
  // whatever renders that list, so its first segment is what identifies the tab.
  return keys.some(key => names.has(key) || names.has(key.split('.')[0]));
};
